/**
 * EventsTab Component
 * 
 * Match events timeline for goals, cards, and substitutions.
 * Supports two views: Block (cards) and Linear (vertical timeline)
 */
import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Plus, X, Clock, LayoutGrid, GitCommitVertical } from 'lucide-react';
import { EVENT_TYPES } from './constants';

/**
 * @param {Object} props
 * @param {Object[]} props.events - Array of event objects
 * @param {Object[]} props.players - Array of player objects
 * @param {boolean} props.isViewOnly - Whether events are read-only
 * @param {Function} props.onAddEvent - Handler to open add event dialog
 * @param {Function} props.onRemoveEvent - Handler to remove an event
 * @param {string} props.buttonColor - Theme button color
 * @param {string} props.teamName - Our team name
 * @param {string} props.opponentName - Opponent team name
 */
export function EventsTab({
  events = [],
  players = [],
  isViewOnly = false,
  onAddEvent,
  onRemoveEvent,
  buttonColor = '#064E3B',
  teamName = 'Our Team',
  opponentName = 'Opponent',
  hasExtraTime = false,
  hasPenalties = false
}) {
  const [viewMode, setViewMode] = useState('block'); // 'block' or 'linear'

  const getPlayerById = (playerId) => {
    return players.find(p => p.id === playerId);
  };

  // Group events by minute and type for combined display
  const processedEvents = [];
  const processedIndices = new Set();

  events.forEach((event, index) => {
    if (processedIndices.has(index)) return;
    
    const eventType = event.event_type || event.type;

    // Shootout events are handled separately, just pass through
    if (eventType === 'pen_scored' || eventType === 'pen_missed') {
      processedEvents.push({ ...event, event_type: eventType, indices: [index] });
      processedIndices.add(index);
      return;
    }

    // Look for goal + assist pairs (regular goal or penalty goal)
    if (eventType === 'goal' || eventType === 'penalty_goal') {
      const assistEvent = events.find((e, i) => 
        i !== index && 
        (e.event_type === 'assist' || e.type === 'assist') && 
        e.minute === event.minute &&
        !processedIndices.has(i) &&
        !e.is_opponent // Only pair assists with same team goals
      );
      
      if (assistEvent && !event.is_opponent) {
        const assistIndex = events.indexOf(assistEvent);
        processedIndices.add(index);
        processedIndices.add(assistIndex);
        processedEvents.push({
          type: 'goal_with_assist',
          goalEvent: event,
          assistEvent: assistEvent,
          minute: event.minute,
          is_opponent: event.is_opponent,
          indices: [index, assistIndex]
        });
      } else {
        processedIndices.add(index);
        processedEvents.push({ ...event, event_type: eventType, indices: [index] });
      }
    }
    // Look for sub out + sub in pairs
    else if (eventType === 'substitution_out') {
      const subInEvent = events.find((e, i) => 
        i !== index && 
        (e.event_type === 'substitution_in' || e.type === 'substitution_in') && 
        e.minute === event.minute &&
        !processedIndices.has(i)
      );
      
      if (subInEvent) {
        const subInIndex = events.indexOf(subInEvent);
        processedIndices.add(index);
        processedIndices.add(subInIndex);
        processedEvents.push({
          type: 'substitution',
          subOutEvent: event,
          subInEvent: subInEvent,
          minute: event.minute,
          indices: [index, subInIndex]
        });
      } else {
        processedIndices.add(index);
        processedEvents.push({ ...event, event_type: eventType, indices: [index] });
      }
    }
    else if (eventType === 'substitution_in') {
      // Check if already paired with a sub out
      const subOutEvent = events.find((e, i) => 
        i !== index && 
        (e.event_type === 'substitution_out' || e.type === 'substitution_out') && 
        e.minute === event.minute &&
        !processedIndices.has(i)
      );
      
      if (subOutEvent) {
        const subOutIndex = events.indexOf(subOutEvent);
        processedIndices.add(index);
        processedIndices.add(subOutIndex);
        processedEvents.push({
          type: 'substitution',
          subOutEvent: subOutEvent,
          subInEvent: event,
          minute: event.minute,
          indices: [subOutIndex, index]
        });
      } else {
        processedIndices.add(index);
        processedEvents.push({ ...event, event_type: eventType, indices: [index] });
      }
    }
    else if (eventType === 'assist') {
      // Standalone assist (not paired with goal)
      processedIndices.add(index);
      processedEvents.push({ ...event, event_type: eventType, indices: [index] });
    }
    else {
      // Other events (cards, etc.)
      processedIndices.add(index);
      processedEvents.push({ ...event, event_type: eventType, indices: [index] });
    }
  });

  // Sort by minute (descending for linear view - latest first)
  const sortedEventsDesc = [...processedEvents].sort((a, b) => b.minute - a.minute);
  // Sort by minute (ascending for block view)
  const sortedEventsAsc = [...processedEvents].sort((a, b) => a.minute - b.minute);

  return (
    <Card className="rounded-2xl" data-testid="events-tab-content">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Match Events</h3>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              <button
                onClick={() => setViewMode('block')}
                className={`p-1.5 rounded-md transition-all ${
                  viewMode === 'block' 
                    ? 'bg-white shadow-sm text-gray-900' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Block View"
                data-testid="view-block-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('linear')}
                className={`p-1.5 rounded-md transition-all ${
                  viewMode === 'linear' 
                    ? 'bg-white shadow-sm text-gray-900' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Timeline View"
                data-testid="view-linear-btn"
              >
                <GitCommitVertical className="w-4 h-4" />
              </button>
            </div>
            {!isViewOnly && (
              <Button
                onClick={onAddEvent}
                className="rounded-xl"
                style={{ 
                  backgroundColor: buttonColor,
                  borderColor: buttonColor
                }}
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add Event
              </Button>
            )}
          </div>
        </div>

        {processedEvents.length > 0 ? (
          viewMode === 'block' ? (
            // Block View (existing)
            <BlockView
              events={sortedEventsAsc}
              getPlayerById={getPlayerById}
              isViewOnly={isViewOnly}
              onRemoveEvent={onRemoveEvent}
            />
          ) : (
            // Linear Timeline View (new)
            <LinearTimelineView
              events={sortedEventsDesc}
              getPlayerById={getPlayerById}
              isViewOnly={isViewOnly}
              onRemoveEvent={onRemoveEvent}
              teamName={teamName}
              opponentName={opponentName}
              buttonColor={buttonColor}
              hasExtraTime={hasExtraTime}
              hasPenalties={hasPenalties}
            />
          )
        ) : (
          <div className="text-center py-12 text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
            <p>No events recorded</p>
            {!isViewOnly && <p className="text-sm mt-1">Add goals, cards, and substitutions</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * BlockView - Card-based event display (existing view)
 */
function BlockView({ events, getPlayerById, isViewOnly, onRemoveEvent }) {
  return (
    <div className="space-y-2">
      {events.map((event, idx) => {
        if (event.type === 'goal_with_assist') {
          const scorer = getPlayerById(event.goalEvent.player_id);
          const assister = getPlayerById(event.assistEvent.player_id);
          return (
            <GoalWithAssistItem
              key={`goal-${idx}`}
              scorer={scorer}
              assister={assister}
              minute={event.minute}
              isViewOnly={isViewOnly}
              onRemove={() => {
                event.indices.sort((a, b) => b - a).forEach(i => onRemoveEvent?.(i));
              }}
            />
          );
        }
        
        if (event.type === 'substitution') {
          const playerOut = getPlayerById(event.subOutEvent.player_id);
          const playerIn = getPlayerById(event.subInEvent.player_id);
          return (
            <SubstitutionItem
              key={`sub-${idx}`}
              playerOut={playerOut}
              playerIn={playerIn}
              minute={event.minute}
              isViewOnly={isViewOnly}
              onRemove={() => {
                event.indices.sort((a, b) => b - a).forEach(i => onRemoveEvent?.(i));
              }}
            />
          );
        }

        const eventTypeValue = event.event_type || event.type;
        const eventTypeInfo = EVENT_TYPES.find(e => e.value === eventTypeValue);
        const player = event.is_opponent ? null : getPlayerById(event.player_id);
        return (
          <EventItem
            key={event.id || idx}
            event={event}
            eventType={eventTypeInfo}
            player={player}
            isOpponent={event.is_opponent}
            isViewOnly={isViewOnly}
            onRemove={() => onRemoveEvent?.(event.indices[0])}
          />
        );
      })}
    </div>
  );
}

/**
 * LinearTimelineView - Vertical timeline with center line
 */
function LinearTimelineView({ events, getPlayerById, isViewOnly, onRemoveEvent, teamName, opponentName, buttonColor, hasExtraTime, hasPenalties }) {
  // Split events by period
  const normalEvents = events.filter(e => !e.period || e.period === 'normal');
  const et1Events = events.filter(e => e.period === 'et1');
  const et2Events = events.filter(e => e.period === 'et2');
  const shootoutEvents = events.filter(e => e.period === 'shootout' || e.event_type === 'pen_scored' || e.event_type === 'pen_missed');
  
  // Normal time split
  const firstHalfEvents = normalEvents.filter(e => e.minute <= 45);
  const secondHalfEvents = normalEvents.filter(e => e.minute > 45);
  
  // Calculate scores at half-time and full-time
  const calculateScore = (eventsList) => {
    let ourGoals = 0;
    let opponentGoals = 0;
    
    eventsList.forEach(event => {
      const eventType = event.event_type || event.type;
      if (eventType === 'goal' || eventType === 'penalty_goal' || event.type === 'goal_with_assist') {
        if (event.is_opponent || event.goalEvent?.is_opponent) {
          opponentGoals++;
        } else {
          ourGoals++;
        }
      } else if (eventType === 'own_goal') {
        if (event.is_opponent) {
          ourGoals++;
        } else {
          opponentGoals++;
        }
      }
    });
    
    return { ourGoals, opponentGoals };
  };
  
  const halfTimeScore = calculateScore(firstHalfEvents);
  const fullTimeScore = calculateScore([...firstHalfEvents, ...secondHalfEvents]);
  const et1Score = calculateScore([...firstHalfEvents, ...secondHalfEvents, ...et1Events]);
  const aetScore = calculateScore([...firstHalfEvents, ...secondHalfEvents, ...et1Events, ...et2Events]);
  
  // Penalty shootout scoring
  const penOurScored = shootoutEvents.filter(e => !e.is_opponent && (e.event_type === 'pen_scored')).length;
  const penOppScored = shootoutEvents.filter(e => e.is_opponent && (e.event_type === 'pen_scored')).length;
  
  const renderEvents = (eventsList) => eventsList.map((event, idx) => {
    const isOpponent = event.is_opponent || event.goalEvent?.is_opponent;
    return (
      <TimelineEventRow
        key={`evt-${idx}`}
        event={event}
        getPlayerById={getPlayerById}
        isOpponent={isOpponent}
        isViewOnly={isViewOnly}
        onRemoveEvent={onRemoveEvent}
      />
    );
  });
  
  return (
    <div className="relative">
      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 transform -translate-x-1/2" />
      
      <div className="space-y-0">
        {/* Penalty Shootout Section */}
        {hasPenalties && (
          <>
            <TimelineMarker label="Penalties" type="main" buttonColor="#DC2626" />
            {shootoutEvents.length > 0 && (
              <PenaltyShootoutDisplay
                events={shootoutEvents}
                getPlayerById={getPlayerById}
                isViewOnly={isViewOnly}
                onRemoveEvent={onRemoveEvent}
                teamName={teamName}
                opponentName={opponentName}
                penOurScored={penOurScored}
                penOppScored={penOppScored}
              />
            )}
          </>
        )}
        
        {/* ET 2nd Half */}
        {hasExtraTime && (
          <>
            <TimelineMarker label={hasPenalties ? "AET" : "Full-Time (AET)"} type="main" buttonColor="#D97706" />
            {hasExtraTime && (
              <ScoreDisplay 
                ourGoals={aetScore.ourGoals} 
                opponentGoals={aetScore.opponentGoals}
                teamName={teamName}
                opponentName={opponentName}
                label="AET"
              />
            )}
            {renderEvents(et2Events)}
            <TimelineMarker label="ET 2nd Half" type="half" buttonColor="#D97706" />
            <ScoreDisplay 
              ourGoals={et1Score.ourGoals} 
              opponentGoals={et1Score.opponentGoals}
              teamName={teamName}
              opponentName={opponentName}
              label="ET HT"
            />
            <TimelineMarker label="ET Half-Time" type="half" buttonColor="#D97706" />
            {renderEvents(et1Events)}
            <TimelineMarker label="ET 1st Half" type="half" buttonColor="#D97706" />
          </>
        )}

        {/* Full-Time marker */}
        <TimelineMarker label={hasExtraTime ? "90 Minutes" : "Full-Time"} type="main" buttonColor={buttonColor} />
        
        <ScoreDisplay 
          ourGoals={fullTimeScore.ourGoals} 
          opponentGoals={fullTimeScore.opponentGoals}
          teamName={teamName}
          opponentName={opponentName}
          label="FT"
        />
        
        {/* Second half events (46'+) */}
        {renderEvents(secondHalfEvents)}
        
        {/* Half-time markers */}
        <TimelineMarker label="2nd Half" type="half" buttonColor={buttonColor} />
        
        <ScoreDisplay 
          ourGoals={halfTimeScore.ourGoals} 
          opponentGoals={halfTimeScore.opponentGoals}
          teamName={teamName}
          opponentName={opponentName}
          label="HT"
        />
        
        <TimelineMarker label="Half-Time" type="half" buttonColor={buttonColor} />
        
        {/* First half events (0-45') */}
        {renderEvents(firstHalfEvents)}
        
        {/* Kick-Off marker */}
        <TimelineMarker label="Kick-Off" type="main" buttonColor={buttonColor} />
      </div>
    </div>
  );
}

/**
 * PenaltyShootoutDisplay - Shows penalty shootout results
 */
function PenaltyShootoutDisplay({ events, getPlayerById, isViewOnly, onRemoveEvent, teamName, opponentName, penOurScored, penOppScored }) {
  // Sort by pen_order
  const sortedPens = [...events].sort((a, b) => (a.pen_order || 0) - (b.pen_order || 0));
  
  return (
    <div className="relative flex items-center justify-center py-3">
      <div className="relative z-10 bg-white border border-red-200 rounded-xl px-4 py-3 shadow-sm w-full max-w-sm mx-auto">
        <div className="text-center mb-2">
          <span className="text-xs text-red-600 font-semibold uppercase tracking-wider">Shootout</span>
          <div className="text-lg font-bold text-gray-900 mt-0.5">{penOurScored} - {penOppScored}</div>
        </div>
        <div className="space-y-1.5">
          {sortedPens.map((pen, idx) => {
            const isScored = pen.event_type === 'pen_scored';
            const player = pen.is_opponent ? null : getPlayerById(pen.player_id);
            return (
              <div key={idx} className={`flex items-center justify-between text-sm px-2 py-1 rounded-lg ${pen.is_opponent ? 'flex-row-reverse' : ''}`}>
                <span className={`font-medium ${pen.is_opponent ? 'text-right' : 'text-left'} flex-1`}>
                  {pen.is_opponent ? opponentName : (player?.name || 'Unknown')}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full mx-2 font-semibold ${
                  isScored ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                }`} data-testid={`pen-result-${idx}`}>
                  {isScored ? 'Scored' : 'Missed'}
                </span>
                <span className="text-xs text-gray-400 w-4 text-center">{pen.pen_order || idx + 1}</span>
                {!isViewOnly && (
                  <button
                    onClick={() => {
                      const eventIdx = pen.indices?.[0];
                      if (eventIdx !== undefined) onRemoveEvent?.(eventIdx);
                    }}
                    className="ml-1 text-gray-300 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * ScoreDisplay - Shows the score at a specific point in the match
 */
function ScoreDisplay({ ourGoals, opponentGoals, teamName, opponentName, label }) {
  return (
    <div className="relative flex items-center justify-center py-2">
      <div className="relative z-10 flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 truncate max-w-[80px]" title={teamName}>
            {teamName}
          </span>
          <span className="text-lg font-bold text-gray-900">{ourGoals}</span>
          <span className="text-gray-400">-</span>
          <span className="text-lg font-bold text-gray-900">{opponentGoals}</span>
          <span className="text-sm font-medium text-gray-700 truncate max-w-[80px]" title={opponentName}>
            {opponentName}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper function to lighten a hex color
 */
function lightenColor(hex, percent) {
  // Fallback to default if hex is undefined/null
  if (!hex) hex = '#064E3B';
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten
  const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
  const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
  const newB = Math.min(255, Math.floor(b + (255 - b) * percent));
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

/**
 * TimelineMarker - Kick-Off, Half-Time, and Full-Time markers with whistle icon
 */
function TimelineMarker({ label, type, buttonColor = '#064E3B' }) {
  // Use team's primary color for main markers, lighter shade for half-time
  const isHalfTime = type === 'half';
  const bgColor = isHalfTime ? lightenColor(buttonColor, 0.3) : buttonColor;
  
  // Simple referee whistle SVG icon matching the reference
  const WhistleIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Whistle body - oval shape */}
      <ellipse cx="9" cy="14" rx="6" ry="5"/>
      {/* Inner circle */}
      <circle cx="9" cy="14" r="2"/>
      {/* Mouthpiece */}
      <path d="M14 12 L20 8 L20 12 L14 14"/>
      {/* Loop at bottom */}
      <circle cx="4" cy="17" r="1.5"/>
      {/* Sound waves */}
      <path d="M16 5 L16 3"/>
      <path d="M18 6 L19 4"/>
      <path d="M14 6 L13 4"/>
    </svg>
  );
  
  return (
    <div className="relative flex items-center justify-center py-3">
      <div 
        className="relative z-10 flex items-center justify-center gap-2 w-32 py-2 text-white rounded-full shadow-md"
        style={{ backgroundColor: bgColor }}
      >
        <WhistleIcon />
        <span className="text-sm font-semibold">{label}</span>
      </div>
    </div>
  );
}

/**
 * TimelineEventRow - Single row in the linear timeline
 */
function TimelineEventRow({ event, getPlayerById, isOpponent, isViewOnly, onRemoveEvent, isFirst, isLast }) {
  const eventType = event.event_type || event.type;
  const eventTypeInfo = EVENT_TYPES.find(e => e.value === eventType);
  
  // Get event details
  let eventLabel = '';
  let playerName = '';
  let icon = eventTypeInfo?.icon || '📝';
  let iconBgColor = 'bg-gray-100';
  let textColor = isOpponent ? 'text-red-600' : 'text-emerald-700';
  
  if (event.type === 'goal_with_assist') {
    const scorer = getPlayerById(event.goalEvent.player_id);
    const assister = getPlayerById(event.assistEvent.player_id);
    const goalType = event.goalEvent.event_type || 'goal';
    eventLabel = goalType === 'penalty_goal' ? 'Penalty' : 'Goal';
    playerName = scorer?.name || 'Unknown';
    icon = '⚽';
    iconBgColor = 'bg-blue-100';
  } else if (event.type === 'substitution') {
    const playerOut = getPlayerById(event.subOutEvent.player_id);
    const playerIn = getPlayerById(event.subInEvent.player_id);
    // For substitution, we'll render two items
    return (
      <>
        <SingleTimelineItem
          minute={event.minute}
          eventLabel="Subbed In"
          playerName={playerIn?.name || 'Unknown'}
          icon={<span className="text-green-500 text-lg">›</span>}
          iconBgColor="bg-white border border-gray-200"
          textColor="text-green-600"
          isOpponent={false}
          isViewOnly={isViewOnly}
          onRemove={() => event.indices.sort((a, b) => b - a).forEach(i => onRemoveEvent?.(i))}
        />
        <SingleTimelineItem
          minute={event.minute}
          eventLabel="Subbed Out"
          playerName={playerOut?.name || 'Unknown'}
          icon={<span className="text-red-500 text-lg">‹</span>}
          iconBgColor="bg-white border border-gray-200"
          textColor="text-red-500"
          isOpponent={false}
          isViewOnly={isViewOnly}
          hideMinute
        />
      </>
    );
  } else if (eventType === 'goal') {
    eventLabel = 'Goal';
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
    icon = '⚽';
    iconBgColor = isOpponent ? 'bg-red-100' : 'bg-blue-100';
  } else if (eventType === 'penalty_goal') {
    eventLabel = 'Penalty';
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
    icon = '⚽';
    iconBgColor = isOpponent ? 'bg-red-100' : 'bg-blue-100';
  } else if (eventType === 'own_goal') {
    eventLabel = 'Own Goal';
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
    icon = '⚽';
    iconBgColor = 'bg-orange-100';
    textColor = 'text-orange-700';
  } else if (eventType === 'missed_penalty') {
    eventLabel = 'Missed Penalty';
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
    icon = '❌';
    iconBgColor = 'bg-gray-100';
    textColor = isOpponent ? 'text-red-400' : 'text-gray-500';
  } else if (eventType === 'yellow_card') {
    eventLabel = 'Yellow Card';
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
    icon = '🟨';
    iconBgColor = 'bg-yellow-50';
  } else if (eventType === 'red_card') {
    eventLabel = 'Red Card';
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
    icon = '🟥';
    iconBgColor = 'bg-red-50';
  } else if (eventType === 'assist') {
    eventLabel = 'Assist';
    playerName = getPlayerById(event.player_id)?.name || 'Unknown';
    icon = '👟';
    iconBgColor = 'bg-emerald-50';
  } else {
    eventLabel = eventTypeInfo?.label || eventType;
    playerName = isOpponent ? 'Opponent' : (getPlayerById(event.player_id)?.name || 'Unknown');
  }

  return (
    <SingleTimelineItem
      minute={event.minute}
      eventLabel={eventLabel}
      playerName={playerName}
      icon={icon}
      iconBgColor={iconBgColor}
      textColor={textColor}
      isOpponent={isOpponent}
      isViewOnly={isViewOnly}
      onRemove={() => event.indices?.sort((a, b) => b - a).forEach(i => onRemoveEvent?.(i))}
      assistName={event.type === 'goal_with_assist' ? getPlayerById(event.assistEvent.player_id)?.name : null}
    />
  );
}

/**
 * SingleTimelineItem - Individual event in the timeline
 */
function SingleTimelineItem({ 
  minute, 
  eventLabel, 
  playerName, 
  icon, 
  iconBgColor, 
  textColor, 
  isOpponent, 
  isViewOnly, 
  onRemove,
  hideMinute = false,
  assistName = null
}) {
  return (
    <div className="relative flex items-center py-3 group">
      {/* Left side (Our Team) */}
      <div className={`flex-1 pr-8 ${isOpponent ? 'invisible' : ''}`}>
        <div className="flex items-center justify-end gap-3">
          <div className="text-right">
            <p className={`text-sm font-semibold ${textColor}`}>{eventLabel}</p>
            <p className="text-sm text-gray-700">{playerName}</p>
            {assistName && (
              <p className="text-xs text-gray-500">Assist: {assistName}</p>
            )}
          </div>
          <div className={`w-8 h-8 rounded-full ${iconBgColor} flex items-center justify-center text-sm`}>
            {typeof icon === 'string' ? icon : icon}
          </div>
        </div>
      </div>
      
      {/* Center - Minute circle */}
      <div className="relative z-10 flex-shrink-0">
        {!hideMinute ? (
          <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-sm">
            <span className="text-sm font-bold text-gray-700">{minute}'</span>
          </div>
        ) : (
          <div className="w-3 h-3 rounded-full bg-gray-300" />
        )}
      </div>
      
      {/* Right side (Opponent) */}
      <div className={`flex-1 pl-8 ${!isOpponent ? 'invisible' : ''}`}>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full ${iconBgColor} flex items-center justify-center text-sm`}>
            {typeof icon === 'string' ? icon : icon}
          </div>
          <div>
            <p className={`text-sm font-semibold ${textColor}`}>{eventLabel}</p>
            <p className="text-sm text-gray-700">{playerName}</p>
          </div>
        </div>
      </div>
      
      {/* Delete button (shown on hover) */}
      {!isViewOnly && onRemove && (
        <button
          onClick={onRemove}
          className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * GoalWithAssistItem - Combined goal and assist display (block view)
 */
function GoalWithAssistItem({ scorer, assister, minute, isViewOnly, onRemove }) {
  return (
    <div
      className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-100"
      data-testid="goal-with-assist-item"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-lg shadow-sm">
          ⚽
        </div>
        <div>
          <p className="font-medium text-gray-900">
            {scorer?.name || 'Unknown'}
          </p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <span>Goal</span>
            <span className="text-gray-300">•</span>
            <span className="text-emerald-600">Assist: {assister?.name || 'Unknown'}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-gray-700">{minute}'</span>
        {!isViewOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * SubstitutionItem - Combined sub in and sub out display (block view)
 */
function SubstitutionItem({ playerOut, playerIn, minute, isViewOnly, onRemove }) {
  return (
    <div
      className="flex items-center justify-between p-3 rounded-xl bg-blue-50 border border-blue-100"
      data-testid="substitution-item"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
          <div className="flex flex-col items-center text-xs">
            <span className="text-green-500">🔼</span>
            <span className="text-red-500">🔽</span>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-green-600 text-xs font-medium">IN</span>
            <span className="font-medium text-gray-900">{playerIn?.name || 'Unknown'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-red-500 text-xs font-medium">OUT</span>
            <span className="text-sm text-gray-500">{playerOut?.name || 'Unknown'}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-gray-700">{minute}'</span>
        {!isViewOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * EventItem - Individual event row (block view)
 */
function EventItem({ event, eventType, player, isOpponent, isViewOnly, onRemove }) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-xl border ${
        isOpponent 
          ? 'bg-red-50 border-red-100' 
          : 'bg-gray-50 border-gray-100'
      }`}
      data-testid={`event-item-${event.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg shadow-sm ${
          isOpponent ? 'bg-red-100' : 'bg-white'
        }`}>
          {eventType?.icon || '📝'}
        </div>
        <div>
          <p className={`font-medium ${isOpponent ? 'text-red-700' : 'text-gray-900'}`}>
            {isOpponent ? 'Opponent' : (player?.name || 'Unknown')}
          </p>
          <p className="text-xs text-gray-500">{eventType?.label || event.type}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-gray-700">{event.minute}'</span>
        {!isViewOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-500"
            data-testid={`remove-event-${event.id}`}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default EventsTab;
